import { Component, Input, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OperatingStatus } from './operating-status.interface';
import { OperatingStatusService } from './operating-status.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';

@Component({
    selector: 'finder-operating-status',
    templateUrl: './operating-status.component.html',
    styleUrls: ['./operating-status.component.scss'],
    // Turn this off so the dynamic html can be styled
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        SafeHtmlPipe
    ],
})
export class OperatingStatusComponent implements OnInit, OnDestroy {
    @Input() labels;
    @Input() data: OperatingStatus;
    @Input() facilityId;
    @Input() usedAsPin = false;

    private destroy$ = new Subject();

    waitTime;
    status;

    constructor(
        private operatingStatusService: OperatingStatusService,
        private zone: NgZone,
    ) {

    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    ngOnInit(): void {
        // Check for a possible new status before updating
        this.data = Object.assign({}, this?.data, this.operatingStatusService.checkForUpdate(this.facilityId));

        this.checkForWaitTime(this.data?.waitTime);
        this.checkForStatus(this.data?.operatingStatus);

        // Wait for updates
        this.operatingStatusService.listenForUpdates$().pipe(
            takeUntil(this.destroy$),
            // Ignore the first placeholder response for the subject
            filter((statusUpdate) => statusUpdate),
            // Filter for messages that match the facility
            filter((statusUpdate: OperatingStatus) => statusUpdate?.facilityId === this.facilityId)
        ).subscribe((statusUpdate: OperatingStatus) => {
            this.checkForWaitTime(statusUpdate?.waitTime);
            this.checkForStatus(statusUpdate?.operatingStatus);
        });
    }

    checkForWaitTime(waitTime) {
        let result;

        // When used as a map pin we should just show two dashes as the time
        if (!waitTime && this.usedAsPin) {
            waitTime = '- -';
        }

        // Stop if there's no wait time
        if (!waitTime) {
            result = void 0;
        } else {
            // Set the wait time
            result = this.labels.waitTime.replace(`{min}`, `<strong>${waitTime}</strong>`);

            // Clear the status
            this.status = void 0;
        }

        /**
         * https://stackoverflow.com/a/57661816/122236
         * This code has to refresh the zones object because it came from outside angular
         **/
        this.zone.run(() => {
            // Merge the wait time and the label
            this.waitTime = result;
        });
    }

    checkForStatus(operatingStatus) {
        let result;

        // We don't want status updates to show on the pin
        if (this.usedAsPin) {
            return;
        }

        if (!operatingStatus) {
            result = void 0;
        } else {
            // Set the status
            result = operatingStatus;

            // Clear the wait time
            this.waitTime = void 0;
        }

        // Just return the value
        this.zone.run(() => {
            this.status = result;
        });
    }
}
